'use client'

import { useEffect, useState, useRef } from 'react'

type UseScrollPropertiesArgs = {
  offset?: number
  debounceDuration?: number
}

type UseScrollPropertiesReturn = {
  isScrolling: boolean
  scrollDirection: 'up' | 'down'
  distanceFromTop: number
  distanceFromBottom: number
}

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: NodeJS.Timeout
  return function (...args: Parameters<T>) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

export default function useScrollProperties(
  settings: UseScrollPropertiesArgs,
): UseScrollPropertiesReturn {
  const [isScrolling, setIsScrolling] = useState(false)
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null)
  const [distanceFromTop, setDistanceFromTop] = useState(0)
  const [distanceFromBottom, setDistanceFromBottom] = useState(0)

  const lastScrollTop = useRef(0)
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null)

  const offset = settings?.offset ?? 0
  const debounceDuration = settings?.debounceDuration ?? 200

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = document.documentElement.clientHeight

      setDistanceFromTop(scrollTop)
      setDistanceFromBottom(scrollHeight - scrollTop - clientHeight)

      if (scrollTop > lastScrollTop.current + offset) {
        setScrollDirection('down')
      } else if (scrollTop < lastScrollTop.current - offset) {
        setScrollDirection('up')
      }

      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop // For Mobile or negative scrolling

      setIsScrolling(true)
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
      scrollTimeout.current = setTimeout(() => setIsScrolling(false), debounceDuration)
    }

    const debouncedHandleScroll = debounce(() => {
      requestAnimationFrame(handleScroll)
    }, 100)

    window.addEventListener('scroll', debouncedHandleScroll)

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll)
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
    }
  }, [offset, debounceDuration])

  return { isScrolling, scrollDirection, distanceFromTop, distanceFromBottom }
}
